import { Component, Input, Output, EventEmitter, OnInit, ChangeDetectionStrategy, inject, ChangeDetectorRef } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { DrawerModule } from 'primeng/drawer';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { CardModule } from 'primeng/card';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TelecomService } from '@generated/services/telecom.service';
import { TelecomAccountListItemResult } from '@generated/models/telecom-account-list-item-result';
import { PhoneNumberResult } from '@generated/models/phone-number-result';
import { NotificationService } from '@core/services/notification.service';
import { IMPORT_NUMBER_FIELDS } from '@core/assistant/assistant-catalog';
import { AssistantActionDirective } from '@shared/directives/assistant-action.directive';
import { AssistantEditableDirective } from '@shared/directives/assistant-editable.directive';
import { AssistantSurfaceDirective } from '@shared/directives/assistant-surface.directive';

@Component({
  selector: 'app-import-twilio-number',
  standalone: true,
  imports: [
    FormsModule,
    DrawerModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    CardModule,
    ProgressSpinnerModule,
    AssistantSurfaceDirective,
    AssistantEditableDirective,
    AssistantActionDirective
],
  templateUrl: './import-twilio-number.html',
  styleUrl: './import-twilio-number.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ImportTwilioNumberComponent implements OnInit {
  /** Идентификаторы полей для агента: опечатку ловит компилятор. */
  protected readonly F = IMPORT_NUMBER_FIELDS;
  private telecomService = inject(TelecomService);
  private notificationService = inject(NotificationService);
  private cdr = inject(ChangeDetectorRef);

  private _visible = false;
  @Input()
  set visible(value: boolean) {
    this._visible = value;
    if (value && this.accounts.length === 0 && !this.accountsProvided) {
      this.loadAccounts();
    }
  }
  get visible(): boolean {
    return this._visible;
  }

  @Input()
  set accounts(value: TelecomAccountListItemResult[]) {
    this._accounts = (value || []).filter(a => a.provider === 'Twilio');
    this.accountsProvided = !!value && value.length > 0;
  }
  get accounts(): TelecomAccountListItemResult[] {
    return this._accounts;
  }

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() imported = new EventEmitter<void>();

  // Accounts
  private _accounts: TelecomAccountListItemResult[] = [];
  private accountsProvided = false;
  selectedAccountId: string | null = null;
  isLoadingAccounts = false;

  // Add new account
  showAddAccountForm = false;
  newAccountSid = '';
  newAccountAuthToken = '';
  isAddingAccount = false;

  // Phone numbers
  phoneNumbers: PhoneNumberResult[] = [];
  selectedPhoneNumber: string | null = null;
  isLoadingPhoneNumbers = false;
  phoneNumbersLoadError = false;

  // Label
  label = '';

  // Import state
  isImporting = false;

  ngOnInit(): void {
    // Load accounts only when drawer is opened
  }

  private loadAccounts(): void {
    this.isLoadingAccounts = true;
    this.cdr.markForCheck();

    this.telecomService.apiTelecomAccountsGet().subscribe({
      next: (accounts) => {
        this._accounts = (accounts || []).filter(a => a.provider === 'Twilio');
        this.isLoadingAccounts = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to load telecom accounts:', error);
        this.notificationService.showError('Failed to load telecom accounts');
        this.isLoadingAccounts = false;
        this.cdr.markForCheck();
      }
    });
  }

  onAccountChange(): void {
    if (this.selectedAccountId) {
      this.loadPhoneNumbers(this.selectedAccountId);
    }
  }

  private loadPhoneNumbers(accountId: string): void {
    this.isLoadingPhoneNumbers = true;
    this.phoneNumbersLoadError = false;
    this.phoneNumbers = [];
    this.selectedPhoneNumber = null;
    this.cdr.markForCheck();

    this.telecomService.apiTelecomTwilioAccountsIdPhoneNumbersGet({ id: accountId }).subscribe({
      next: (phoneNumbers) => {
        this.phoneNumbers = phoneNumbers || [];
        this.isLoadingPhoneNumbers = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to load phone numbers:', error);
        this.notificationService.showError('Failed to load phone numbers');
        this.phoneNumbersLoadError = true;
        this.isLoadingPhoneNumbers = false;
        this.cdr.markForCheck();
      }
    });
  }

  reloadPhoneNumbers(): void {
    if (this.selectedAccountId) {
      this.loadPhoneNumbers(this.selectedAccountId);
    }
  }

  showAddAccountFormClick(): void {
    this.showAddAccountForm = true;
    this.cdr.markForCheck();
  }

  cancelAddAccount(): void {
    this.showAddAccountForm = false;
    this.newAccountSid = '';
    this.newAccountAuthToken = '';
    this.cdr.markForCheck();
  }

  addNewAccount(): void {
    if (!this.newAccountSid || !this.newAccountAuthToken) {
      this.notificationService.showError('Account SID and Auth Token are required');
      return;
    }

    this.isAddingAccount = true;
    this.cdr.markForCheck();

    this.telecomService.apiTelecomTwilioAccountsPost({
      body: {
        account_sid: this.newAccountSid,
        auth_token: this.newAccountAuthToken
      }
    }).subscribe({
      next: (account) => {
        this.notificationService.showSuccess('Twilio account added successfully');
        this._accounts.push(account);
        this.selectedAccountId = account.id || null;
        this.showAddAccountForm = false;
        this.newAccountSid = '';
        this.newAccountAuthToken = '';
        this.isAddingAccount = false;
        this.cdr.markForCheck();

        if (this.selectedAccountId) {
          this.loadPhoneNumbers(this.selectedAccountId);
        }
      },
      error: (error) => {
        console.error('Failed to add Twilio account:', error);
        this.notificationService.showError('Failed to add Twilio account');
        this.isAddingAccount = false;
        this.cdr.markForCheck();
      }
    });
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  import(): void {
    if (!this.selectedPhoneNumber || !this.selectedAccountId) {
      this.notificationService.showError('Please select a phone number');
      return;
    }

    this.isImporting = true;
    this.cdr.markForCheck();

    this.telecomService.apiTelecomPhoneNumberAgentsPost({
      body: {
        telecom_account_id: this.selectedAccountId,
        phone_number: this.selectedPhoneNumber,
        label: this.label || null
      }
    }).subscribe({
      next: () => {
        this.notificationService.showSuccess('Phone number imported successfully');
        this.isImporting = false;
        this.imported.emit();
        this.close();
        this.resetForm();
      },
      error: (error) => {
        console.error('Failed to import phone number:', error);
        this.notificationService.showError('Failed to import phone number');
        this.isImporting = false;
        this.cdr.markForCheck();
      }
    });
  }

  private resetForm(): void {
    this.label = '';
    this.selectedAccountId = null;
    this.selectedPhoneNumber = null;
    this.phoneNumbers = [];
    this.showAddAccountForm = false;
    this.newAccountSid = '';
    this.newAccountAuthToken = '';
  }

  get accountOptions() {
    return this.accounts.map(acc => ({
      label: acc.account_identifier || acc.id,
      value: acc.id
    }));
  }

  get phoneNumberOptions() {
    return this.phoneNumbers.map(pn => ({
      label: `${pn.friendly_name || pn.phone_number} (${pn.phone_number})`,
      value: pn.phone_number
    }));
  }
}
